import type { TerritorialInspectionSummary } from "@/types/territorial-inspection";
import { ConfidenceBadge, FieldValidationStatusBadge } from "../../inspecoes/components/TerritorioBadges";

interface QuadrasGridProps {
  inspections: TerritorialInspectionSummary[];
  onOpen: (inspection: TerritorialInspectionSummary) => void;
}

function canopyTone(value: number | null): string {
  if (value === null) return "text-foreground-400";
  if (value >= 15) return "text-green-600";
  if (value >= 7) return "text-caution-600";
  return "text-danger-600";
}

export default function QuadrasGrid({ inspections, onOpen }: QuadrasGridProps) {
  const sorted = [...inspections].sort((a, b) => (a.blockCode || "").localeCompare(b.blockCode || ""));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {sorted.map((inspection) => (
        <button
          key={inspection.uuid}
          onClick={() => onOpen(inspection)}
          className="text-left border border-background-200/70 rounded-card p-4 hover:border-primary-300 hover:shadow-sm transition-all bg-white cursor-pointer"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-sm font-semibold text-foreground-900">Quadra {inspection.blockCode || "—"}</p>
              <p className="text-xs text-foreground-500 truncate max-w-[160px]">{inspection.neighborhood || "—"}</p>
            </div>
            <ConfidenceBadge score={inspection.confidenceScore} />
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div>
              <p className="text-foreground-400">Cobertura arbórea</p>
              <p className={`font-semibold ${canopyTone(inspection.canopyCoveragePercent)}`}>
                {inspection.canopyCoveragePercent !== null ? `${inspection.canopyCoveragePercent}%` : "—"}
              </p>
            </div>
            <div>
              <p className="text-foreground-400">Árvores estimadas</p>
              <p className="font-semibold text-foreground-800">{inspection.estimatedTreeCount ?? "—"}</p>
            </div>
            <div>
              <p className="text-foreground-400">Potencial de plantio</p>
              <p className="font-semibold text-foreground-800">{inspection.plantingPotentialCount ?? "—"}</p>
            </div>
            <div>
              <p className="text-foreground-400">Déficit arbóreo</p>
              <p className="font-semibold text-foreground-800">{inspection.treeDeficitCount ?? "—"}</p>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-background-100 flex items-center justify-between">
            <span className="text-[11px] font-mono text-foreground-500">{inspection.inspectionCode}</span>
            <FieldValidationStatusBadge status={inspection.fieldValidationStatus} />
          </div>
        </button>
      ))}
    </div>
  );
}
